import { ApiKeyStatus } from '@today/types'
import { Badge, type BadgeProps, Button } from '@today/ui-primitives'
import { type ReactElement, useId } from 'react'

/**
 * One personal API key as the section lists it (TO-516 REQ-015). Dates are
 * display strings the app has already formatted.
 *
 * @property lastUsedAt - `null` = never used (the section shows its "never" label).
 * @property revocable - False once the key is revoked: the Revoke action is not offered.
 */
export interface ApiKeyRowView {
	apiKeyId: string
	name: string
	keyPrefix: string
	status: ApiKeyStatus
	createdAt: string
	expiresAt: string
	lastUsedAt: string | null
	revocable: boolean
}

/** Copy of {@link ApiKeysSectionView}; each omitted key falls back to English. */
export interface ApiKeysSectionLabels {
	title: string
	description: string
	create: string
	empty: string
	loading: string
	loadFailed: string
	retry: string
	name: string
	prefix: string
	status: string
	created: string
	expires: string
	lastUsed: string
	actions: string
	never: string
	rename: string
	revoke: string
	statuses: Record<ApiKeyStatus, string>
}

const DEFAULT_LABELS: ApiKeysSectionLabels = {
	title: 'API keys',
	description:
		'Personal keys let your integrations call the API as you. A key never has more access than you, and stops working when you lose access.',
	create: 'Create API key',
	empty: 'No API keys yet.',
	loading: 'Loading API keys…',
	loadFailed: 'Could not load your API keys.',
	retry: 'Try again',
	name: 'Name',
	prefix: 'Prefix',
	status: 'Status',
	created: 'Created',
	expires: 'Expires',
	lastUsed: 'Last used',
	actions: 'Actions',
	never: 'Never',
	rename: 'Rename',
	revoke: 'Revoke',
	statuses: {
		[ApiKeyStatus.ACTIVE]: 'Active',
		[ApiKeyStatus.REVOKED]: 'Revoked',
		[ApiKeyStatus.EXPIRED]: 'Expired',
		[ApiKeyStatus.IDLE]: 'Idle',
	},
}

const STATUS_VARIANT: Record<ApiKeyStatus, NonNullable<BadgeProps['variant']>> = {
	[ApiKeyStatus.ACTIVE]: 'success',
	[ApiKeyStatus.REVOKED]: 'danger',
	[ApiKeyStatus.EXPIRED]: 'muted',
	[ApiKeyStatus.IDLE]: 'muted',
}

/**
 * Props for {@link ApiKeysSectionView}.
 *
 * @property canCreate - Shows Create and Rename (`api_key.create`).
 * @property canRevoke - Shows Revoke on revocable rows (`api_key.revoke`).
 */
export interface ApiKeysSectionViewProps {
	keys: readonly ApiKeyRowView[]
	isLoading: boolean
	loadFailed: boolean
	canCreate: boolean
	canRevoke: boolean
	onRetry: () => void
	onCreate: () => void
	onRename: (apiKeyId: string) => void
	onRevoke: (apiKeyId: string) => void
	labels?: Partial<ApiKeysSectionLabels>
}

const CELL = 'px-3 py-2 text-left align-middle'

type BodyProps = ApiKeysSectionViewProps & { text: ApiKeysSectionLabels }

function KeyActions({ row, ...props }: BodyProps & { row: ApiKeyRowView }): ReactElement {
	const { text } = props
	return (
		<td className={CELL}>
			<div className="flex gap-2">
				{props.canCreate ? (
					<Button
						variant="secondary"
						size="sm"
						aria-label={`${text.rename} ${row.name}`}
						onPress={() => props.onRename(row.apiKeyId)}
					>
						{text.rename}
					</Button>
				) : null}
				{props.canRevoke && row.revocable ? (
					<Button
						variant="danger"
						size="sm"
						aria-label={`${text.revoke} ${row.name}`}
						onPress={() => props.onRevoke(row.apiKeyId)}
					>
						{text.revoke}
					</Button>
				) : null}
			</div>
		</td>
	)
}

function KeysTable(props: BodyProps): ReactElement {
	const { text } = props
	const showActions = props.canCreate || props.canRevoke
	const headers = [text.name, text.prefix, text.status, text.created, text.expires, text.lastUsed]
	return (
		<div className="overflow-x-auto">
			<table className="w-full border-collapse text-ds-sm text-ds-text">
				<thead className="border-b border-ds-border text-ds-text-muted">
					<tr>
						{(showActions ? [...headers, text.actions] : headers).map((header) => (
							<th key={header} scope="col" className={CELL}>
								{header}
							</th>
						))}
					</tr>
				</thead>
				<tbody>
					{props.keys.map((row) => (
						<tr key={row.apiKeyId} className="border-b border-ds-border">
							<td className={`${CELL} font-medium`}>{row.name}</td>
							<td className={CELL}>
								<code className="font-mono">{row.keyPrefix}</code>
							</td>
							<td className={CELL}>
								<Badge variant={STATUS_VARIANT[row.status]} size="sm" data-status={row.status}>
									{text.statuses[row.status]}
								</Badge>
							</td>
							<td className={CELL}>{row.createdAt}</td>
							<td className={CELL}>{row.expiresAt}</td>
							<td className={CELL}>{row.lastUsedAt ?? text.never}</td>
							{showActions ? <KeyActions {...props} row={row} /> : null}
						</tr>
					))}
				</tbody>
			</table>
		</div>
	)
}

function KeysBody(props: BodyProps): ReactElement {
	const { text } = props
	if (props.loadFailed) {
		return (
			<div role="alert" className="flex flex-col items-start gap-3 text-ds-sm text-ds-danger">
				<p>{text.loadFailed}</p>
				<Button variant="secondary" size="sm" onPress={props.onRetry}>
					{text.retry}
				</Button>
			</div>
		)
	}
	if (props.isLoading) {
		return (
			<p className="text-ds-sm text-ds-text-muted" aria-live="polite">
				{text.loading}
			</p>
		)
	}
	if (props.keys.length === 0) return <p className="text-ds-sm text-ds-text-muted">{text.empty}</p>
	return <KeysTable {...props} />
}

/** The Settings "API keys" section: the owner's keys and their actions (organism, TO-516). */
export function ApiKeysSectionView(props: ApiKeysSectionViewProps): ReactElement {
	const text = { ...DEFAULT_LABELS, ...props.labels }
	const titleId = useId()
	return (
		<section
			aria-labelledby={titleId}
			className="flex flex-col gap-4 rounded-ds-lg border border-ds-border bg-ds-surface p-6"
		>
			<header className="flex flex-wrap items-start justify-between gap-4">
				<div className="flex flex-col gap-1">
					<h2 id={titleId} className="text-ds-lg font-semibold text-ds-text">
						{text.title}
					</h2>
					<p className="text-ds-sm text-ds-text-muted">{text.description}</p>
				</div>
				{props.canCreate ? <Button onPress={props.onCreate}>{text.create}</Button> : null}
			</header>
			<KeysBody {...props} text={text} />
		</section>
	)
}
